import { AloftPrice } from '@aloft/toolkit-shared';
import { Typography, colors } from '@aloft/toolkit-sui';

import { CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceDot } from 'recharts';

import { GraphTooltipWrapper } from '../components/GraphTooltipWrapper.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { GraphProps } from '../types.ts';

type SensitivityGraphPoint = {
  rate: AloftPrice;
  spreadRatio: number;
};

type SensitivityLineGraphProps = GraphProps & {
  data: SensitivityGraphPoint[];
  selectedRate: AloftPrice;
  xLabel: string;
  // Per-feature reliability boundaries (as fractions, e.g. 0.15 = 15%).
  // Used to render the chart bands at the correct thresholds for each feature.
  absoluteThreshold: number;
  conditionalZoneMax: number;
};

// Fixed lower-band thresholds (as %) — these do not vary by feature.
const EXCELLENT_MAX = 5; // < 5%
const GOOD_MAX = 10; // 5–10%

const toFill = (hex: string) => `${hex}1A`; // 10% opacity

const StarDot = (props: { cx?: number; cy?: number }) => {
  const { cx, cy } = props;
  if (cx === undefined || cy === undefined) return null;

  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={14}>
      ★
    </text>
  );
};

export const SensitivityLineGraph = ({
  data,
  selectedRate,
  xLabel,
  absoluteThreshold,
  conditionalZoneMax,
  disableAnimations,
  width,
  height,
}: SensitivityLineGraphProps) => {
  // Convert per-feature fractional thresholds to percentages for the chart
  const acceptableMax = absoluteThreshold * 100;
  const unreliableMax = conditionalZoneMax * 100;
  const UNRELIABLE_CEILING = 100;
  const chartData = data.map((d) => ({
    rate: d.rate.toDollarLike(),
    spreadPct: d.spreadRatio * 100,
  }));

  const selectedRateDollars = selectedRate.toDollarLike();
  const selectedPoint = chartData.find((d) => d.rate === selectedRateDollars);
  const selectedSpreadPct = selectedPoint?.spreadPct;

  const maxRate = chartData.length > 0 ? Math.max(...chartData.map((d) => d.rate)) : 0;

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={chartData}
        margin={{ top: 20, left: 16, bottom: 8, right: 16 }}
        style={{ background: 'transparent' }}
      >
        <CartesianGrid />

        {/* Reliability stratums — rendered bottom-to-top so borders layer correctly */}
        <ReferenceArea
          y1={0}
          y2={EXCELLENT_MAX}
          fill={toFill(colors.graphBeneficial)}
          stroke={colors.graphBeneficial}
          strokeDasharray="4 3"
          strokeOpacity={0.6}
          ifOverflow="visible"
        />
        <ReferenceArea
          y1={EXCELLENT_MAX}
          y2={GOOD_MAX}
          fill={toFill(colors.graphChart)}
          stroke="none"
          ifOverflow="visible"
        />
        <ReferenceArea
          y1={GOOD_MAX}
          y2={acceptableMax}
          fill={toFill(colors.graphChart4)}
          stroke="none"
          ifOverflow="visible"
        />
        <ReferenceArea
          y1={acceptableMax}
          y2={unreliableMax}
          fill={toFill(colors.graphChart3)}
          stroke="none"
          ifOverflow="visible"
        />
        <ReferenceArea
          y1={unreliableMax}
          y2={UNRELIABLE_CEILING}
          fill={toFill(colors.graphChart3)}
          stroke={colors.graphChart3}
          strokeDasharray="4 3"
          strokeOpacity={0.6}
          ifOverflow="visible"
        />

        <GraphXAxis dataKey="rate" label={xLabel} type="number" domain={[0, maxRate]} />
        <GraphYAxis
          dataKey="spreadPct"
          label="Percent Variance"
          type="number"
          tickFormatter={(v: number) => `${v.toFixed(1)}%`}
        />

        <GraphTooltipWrapper disableAnimations={disableAnimations}>
          {({ payload }) => {
            if (!payload?.length) return null;
            const point = (payload[0] as { payload: { rate: number; spreadPct: number } }).payload;
            return (
              <>
                <Typography variant="body2">
                  {xLabel}: ${point.rate.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Percent Variance: {point.spreadPct.toFixed(1)}%
                </Typography>
              </>
            );
          }}
        </GraphTooltipWrapper>

        <Line
          type="monotone"
          dataKey="spreadPct"
          stroke={colors.graphAdverse}
          strokeWidth={2}
          dot={false}
          isAnimationActive={!disableAnimations}
          legendType="none"
        />

        {selectedSpreadPct !== undefined && (
          <ReferenceDot
            x={selectedRateDollars}
            y={selectedSpreadPct}
            shape={<StarDot />}
            ifOverflow="visible"
          />
        )}
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
